import Model from 'models/Model'
import React from 'react'

// Helpers
import { date, dateDistanceToNow, parseDate } from 'helpers/format'

// Icons
import IconNotificationEcwt from 'ui/Icons/IconNotificationEcwt'
import IconNotificationAnnouncement from 'ui/Icons/IconNotificationAnnouncement'
import IconNotificationConcern from 'ui/Icons/IconNotificationConcern'
import IconNotificationPayment from 'ui/Icons/IconNotificationPayment'

// Notification status
export const STATUS_READED = 'readed'

// Notification types
export const TYPE_PAYMENT = 'payment'
export const TYPE_ECWT = 'ecwt'
export const TYPE_CONTRACT_EXPIRATION = 'contract-expiration'
export const TYPE_ANNOUNCEMENT = 'announcement'
export const TYPE_TICKET_COMMENT = 'ticket-comment'
export const TYPE_TICKET_CONFIRM = 'ticket-confirm'

// Notification title
export const NOTIFY_PAYMENT_TITLE = 'New payment record is available!'
export const NOTIFY_ANNOUNCEMENT_TITLE = 'New announcement posted!'
export const NOTIFY_ECWT_TITLE = 'New eCWT record is available!'
export const NOTIFY_CONTRACT_EXPIRATION_TITLE =
  'Your contract will be expiring soon'
export const NOTIFY_CONTRACT_EXPIRATION_EXPIRE_IN_TITLE =
  'Your contract is expiring in '
export const NOTIFY_TICKET_COMMENT_TITLE = 'Help response: New comment added'
export const NOTIFY_TICKET_CONFIRM_TITLE =
  'Help response: New comment & confirm if resolved'

export default class Notification extends Model {
  get id() {
    return this.model.id
  }

  get type() {
    return this.model.type
  }

  get mappingId() {
    return this.model.mappingId
  }

  get subject() {
    return this.model.title
  }

  get content() {
    return this.model.content
  }

  get createdAt() {
    return this.model.createDate
  }

  get contractNumber() {
    return this.model.contractNumber
  }

  get lessorName() {
    return this.model.lessorName
  }

  get companyCode() {
    return this.model.contractCompanny
  }

  get vendorCode() {
    return this.model.vendorCode
  }

  get paymentMethod() {
    return this.model.paymentMethod
  }

  get ticketNo() {
    return this.model.ticketNumber
  }

  get issueCategory() {
    return this.model.issueCategory
  }

  get issueType() {
    return this.model.issueType
  }

  get expireIn() {
    return this.model.expireIn
  }

  get isRead() {
    return this.model.status === STATUS_READED
  }

  get icon() {
    if (this.isPayment) {
      return <IconNotificationPayment />
    }

    if (this.isEcwt || this.isContractExpiration) {
      return <IconNotificationEcwt />
    }

    if (this.isTicketComment || this.isTicketConfirm) {
      return <IconNotificationConcern />
    }

    if (this.isAnnouncement) {
      return <IconNotificationAnnouncement />
    }

    return null
  }

  get isPayment() {
    return this.type === TYPE_PAYMENT
  }

  get isEcwt() {
    return this.type === TYPE_ECWT
  }

  get isContractExpiration() {
    return this.type === TYPE_CONTRACT_EXPIRATION
  }

  get isAnnouncement() {
    return this.type === TYPE_ANNOUNCEMENT
  }

  get isTicketComment() {
    return this.type === TYPE_TICKET_COMMENT
  }

  get isTicketConfirm() {
    return this.type === TYPE_TICKET_CONFIRM
  }

  get createdTime() {
    if (this.createdAt) {
      return parseDate(this.createdAt).getTime()
    }

    return 0
  }

  get title() {
    if (this.isAnnouncement) {
      return NOTIFY_ANNOUNCEMENT_TITLE
    }

    if (this.isPayment) {
      return NOTIFY_PAYMENT_TITLE
    }

    if (this.isEcwt) {
      return NOTIFY_ECWT_TITLE
    }

    if (this.isContractExpiration) {
      if (this.expireIn) {
        return NOTIFY_CONTRACT_EXPIRATION_EXPIRE_IN_TITLE + this.expireIn
      }

      return NOTIFY_CONTRACT_EXPIRATION_EXPIRE_IN_TITLE
    }

    if (this.isTicketComment) {
      return NOTIFY_TICKET_COMMENT_TITLE
    }

    if (this.isTicketConfirm) {
      return NOTIFY_TICKET_CONFIRM_TITLE
    }

    return ''
  }

  get publishedAt() {
    if (this.createdAt) {
      return `
      ${date(this.createdAt, 'MMM dd, yyyy')} |
      ${date(this.createdAt, 'hh:mma')}`
    }

    return ''
  }

  get displayDate() {
    if (this.createdAt) {
      return `
      ${date(this.createdAt, 'MMM dd, yyyy')} |
      ${date(this.createdAt, 'hh:mma')}
      (${dateDistanceToNow(this.createdAt)})`
    }

    return ''
  }

  get raisedAt() {
    return date(this.model.dateRaised, 'MMM dd, yyyy')
  }
}
